import React from "react";

const MODERN_NAV_ITEMS = [
  { key: "about", label: "About" },
  { key: "facilities", label: "Facilities" },
  { key: "grounds", label: "Spaces" },
  { key: "gallery", label: "Gallery" },
  { key: "location", label: "Location" },
];

const ModernTheme = ({
  venue,
  grounds = [],
  website,
  backgroundStyle = "clean",
  primaryColor = "#33A444",
  accentColor = "#111827",
  heroImage = "",
  headline,
  subheadline,
  ctaLabel = "Book Now",
  openBooking,
  sectionRenderers = {},
  themeLayout = "modern",
  selectedThemeLayout = "modern",
}) => {
  const bookingEnabled = website?.booking?.enabled !== false;

  const visibleNavigation = MODERN_NAV_ITEMS.filter(
    (item) => website?.sections?.[item.key] !== false
  );

  const orderedSections = Array.isArray(website?.sectionOrder)
    ? website.sectionOrder
        .filter((key) => key !== "hero")
        .filter((key) => website?.sections?.[key] !== false)
    : [];

  const city = venue?.location?.city || venue?.city || "Your city";
  const openingTime = venue?.policies?.openTime || "06:00";
  const closingTime = venue?.policies?.closeTime || "22:00";

  const sports = [
    ...new Set(
      grounds
        .map(
          (ground) =>
            ground?.sportName ||
            ground?.sport?.name ||
            ground?.sportType
        )
        .filter(Boolean)
    ),
  ];

  return (
    <main
      id="top"
      className={`pv-page pv-theme-${themeLayout} pv-bg-${backgroundStyle}`}
      data-theme={themeLayout}
      data-selected-theme={selectedThemeLayout}
      style={{
        "--pv-primary": primaryColor,
        "--pv-accent": accentColor,
      }}
    >
      <header className="pvm-nav-shell">
        <nav className="pvm-nav" aria-label="Venue website navigation">
          <a className="pvm-brand" href="#top">
            <span className="pvm-brand-mark" aria-hidden="true">
              <span />
            </span>

            <span className="pvm-brand-copy">
              <strong>{venue?.name || "Venue"}</strong>
              <small>{city}</small>
            </span>
          </a>

          <div className="pvm-nav-links">
            <a href="#top">Home</a>

            {visibleNavigation.map((item) => (
              <a href={`#${item.key}`} key={item.key}>
                {item.label}
              </a>
            ))}
          </div>

          {bookingEnabled && (
            <button
              type="button"
              className="pvm-nav-cta"
              onClick={() => openBooking?.()}
            >
              {ctaLabel}
              <span aria-hidden="true">↗</span>
            </button>
          )}
        </nav>
      </header>

      {website?.sections?.hero !== false && (
        <section className="pvm-hero">
          <div className="pvm-hero-glow pvm-hero-glow--one" />
          <div className="pvm-hero-glow pvm-hero-glow--two" />

          <div className="pvm-hero-copy">
            <div className="pvm-status-pill">
              <span />
              Online booking available
            </div>

            <h1>{headline}</h1>

            <p>{subheadline}</p>

            <div className="pvm-hero-actions">
              {bookingEnabled && (
                <button
                  type="button"
                  className="pvm-primary-action"
                  onClick={() => openBooking?.()}
                >
                  {ctaLabel}
                  <span aria-hidden="true">→</span>
                </button>
              )}

              {venue?.googleMapsUrl && (
                <a
                  className="pvm-secondary-action"
                  href={venue.googleMapsUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Explore location
                </a>
              )}
            </div>

            <div className="pvm-hero-trust">
              <div>
                <strong>{grounds.length}</strong>
                <span>Bookable spaces</span>
              </div>

              <div>
                <strong>{sports.length || 1}</strong>
                <span>Sports available</span>
              </div>

              <div>
                <strong>{openingTime}</strong>
                <span>Open from</span>
              </div>
            </div>
          </div>

          <div className="pvm-hero-visual">
            <div className="pvm-image-shell">
              {heroImage ? (
                <img src={heroImage} alt={venue?.name || "Sports venue"} />
              ) : (
                <div className="pvm-image-placeholder">
                  <span>Discover</span>
                  <strong>{venue?.name || "Your Venue"}</strong>
                </div>
              )}

              <div className="pvm-image-overlay" />

              <div className="pvm-image-caption">
                <div>
                  <span>Now accepting</span>
                  <strong>Online reservations</strong>
                </div>

                <span className="pvm-caption-arrow" aria-hidden="true">
                  ↗
                </span>
              </div>
            </div>

            <div className="pvm-floating-card pvm-floating-card--availability">
              <span className="pvm-floating-icon">
                <i />
              </span>

              <div>
                <small>Today’s hours</small>
                <strong>
                  {openingTime} – {closingTime}
                </strong>
              </div>
            </div>

            <div className="pvm-floating-card pvm-floating-card--grounds">
              <strong>{grounds.length}</strong>

              <div>
                <small>Available</small>
                <span>grounds</span>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="pvm-value-grid" aria-label="Venue benefits">
        <article>
          <span className="pvm-value-number">01</span>
          <div>
            <strong>Fast reservation</strong>
            <p>Select your ground, date and preferred available slots.</p>
          </div>
        </article>

        <article>
          <span className="pvm-value-number">02</span>
          <div>
            <strong>Flexible booking</strong>
            <p>Reserve one or multiple slots through the same booking flow.</p>
          </div>
        </article>

        <article>
          <span className="pvm-value-number">03</span>
          <div>
            <strong>Venue information</strong>
            <p>Review facilities, pricing, policies and location before booking.</p>
          </div>
        </article>
      </section>

      <div className="pv-content pvm-content">
        {orderedSections.map((key) => {
          const renderSection = sectionRenderers[key];

          return renderSection ? (
            <React.Fragment key={key}>
              {renderSection()}
            </React.Fragment>
          ) : null;
        })}
      </div>

      <section className="pvm-final-cta">
        <div className="pvm-final-cta-copy">
          <span>Ready when you are</span>
          <h2>Book your next game in a few simple steps.</h2>

          <p>
            Select a venue space, choose the available time slots and continue
            through the existing secure reservation flow.
          </p>
        </div>

        {bookingEnabled && (
          <button type="button" onClick={() => openBooking?.()}>
            <span>{ctaLabel}</span>
            <strong aria-hidden="true">↗</strong>
          </button>
        )}
      </section>

      <footer className="pvm-footer">
        <div className="pvm-footer-top">
          <div className="pvm-footer-brand">
            <span className="pvm-brand-mark" aria-hidden="true">
              <span />
            </span>

            <div>
              <strong>{venue?.name || "Venue"}</strong>
              <small>{city}</small>
            </div>
          </div>

          <p>
            Discover facilities, check live availability and reserve your
            preferred sports slots online.
          </p>
        </div>

        <div className="pvm-footer-bottom">
          <span>
            © {new Date().getFullYear()} {venue?.name || "Venue"}
          </span>

          <span>
            Open {openingTime} – {closingTime}
          </span>

          <span>Powered by OnGround</span>
        </div>
      </footer>
    </main>
  );
};

export default ModernTheme;